import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FileText, Link2, Download, Pen, Search, CheckCircle2, Loader2, Truck, ClipboardCheck, ListChecks, FileStack,
} from "lucide-react";
import MasterBolSignPanel, { MasterBolSignedNote, type MasterBolForSign } from "../../components/trucking/MasterBolSignPanel";
import TruckingInvoiceCard from "../../components/trucking/TruckingInvoiceCard";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  useMoveJob,
  useLinkBolToMoveJob,
  useGenerateMasterBol,
  type MoveJob,
} from "../../data/truckingData";
import { api, getApiErrorMessage, getBlobApiErrorMessage } from "../../api/client";
import { claimBlobTab, POPUP_BLOCKED_MESSAGE, showBlobInTab } from "../../lib/blob-tab";
import { formatCalendarDate, formatInstantDay } from "../../lib/calendar-date";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
// A move job's BOLs are rows of `loads`, so they take the shared load_status
// badge. The local map knew pending, delivered and signed only, and every other
// status (cancelled included) rendered as "Pending".
import LoadStatusBadge from "../../components/trucking/LoadStatusBadge";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { getLoadTypeLabel } from "../../constants/load-types";
import { displayValue } from "../../features/fieldiq-handoff/contracts";

/**
 * GET /trucking/move-jobs/:id sends `mj.*` plus `operator_name` and
 * `coordinator_name`, the linked loads as `bols` (with `truck_unit_number`,
 * `driver_first_name`, `driver_last_name`, `load_type`), and the master BOL as
 * `masterBol` (or null). This page read `operator`, `coordinator`,
 * `truck_unit`, `driver_name` and `cargo`, none of which are sent.
 *
 * The master-BOL actions read `masterBol` (#815). They used to gate on
 * `master_bol_id` and `master_bol_signed`, which are not sent, so Download,
 * Sign and Invoice could never appear; that gate stayed shut until the Sign
 * (400) and PDF (401) writes were repaired, here. Signing is offered when the
 * master BOL is `complete`, which is what the sign route requires; it is
 * signed once `finalized`. Once it is, the Invoice panel offers Generate
 * Invoice (POST /trucking/invoices/from-move-job), or opens the invoice.
 *
 * Signing takes a drawn signature or "Signed on paper" (api#941); see
 * MasterBolSignPanel. `masterBol` is the whole `master_bols` row, so its `id`
 * (the upload's entity id) and how it was signed are both on it.
 */
type MasterBol = (MasterBolForSign & {
  master_bol_number?: string | null;
  total_loads?: number | null;
  loads_signed?: number | null;
}) | null;

type Skin = { label: string; fill: string };
/** Exhaustive over `move_job_status`; unknown is a slate dash, not Planned. */
const JOB_STATUS: Record<string, Skin> = {
  planned: { label: "Planned", fill: "bg-fi-signal-amber" },
  active: { label: "Active", fill: "bg-fi-signal-blue" },
  complete: { label: "Complete", fill: "bg-fi-signal-green" },
};
/** Exhaustive over `master_bol_status`. Complete and Finalized share green; the label separates them. */
const MASTER_STATUS: Record<string, Skin> = {
  draft: { label: "Draft", fill: "bg-fi-signal-slate" },
  in_progress: { label: "In Progress", fill: "bg-fi-signal-blue" },
  complete: { label: "Complete", fill: "bg-fi-signal-green" },
  finalized: { label: "Finalized", fill: "bg-fi-signal-green" },
};
const UNKNOWN: Skin = { label: "—", fill: "bg-fi-signal-slate" };
const skin = (map: Record<string, Skin>, s: unknown) =>
  typeof s === "string" && Object.prototype.hasOwnProperty.call(map, s) ? map[s] : UNKNOWN;

const str = (v: unknown): string | null => (v === null || v === undefined || v === "" ? null : String(v));
const driverOf = (b: Record<string, unknown>) =>
  [str(b.driver_first_name), str(b.driver_last_name)].filter(Boolean).join(" ") || null;

/* ── Link BOL dialog ── */
/**
 * The list is `GET /trucking/loads/bols?move_job_id=unlinked` (api#947): BOLs on
 * no move job. This job's own BOLs and another job's are both left out by the
 * API, so nothing is filtered out here; the search over the BOL number is only
 * a narrowing of what the API returned (the route reads no `search`).
 *
 * A link that races another (the BOL went onto a different move job after the
 * list loaded) is 409 `BOL_ON_OTHER_MOVE_JOB`, naming that job. Its message is
 * shown in the dialog as sent, and the list is reloaded so the BOL drops out.
 */
const LinkBolDialog = ({
  open, onOpenChange, moveJobId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  moveJobId: string;
}) => {
  const [search, setSearch] = useState("");
  const [refusal, setRefusal] = useState<string | null>(null);
  const linkBol = useLinkBolToMoveJob();
  const qc = useQueryClient();

  const listQuery = useQuery({
    queryKey: ['available-bols', 'unlinked'],
    queryFn: () =>
      api.trucking.bols.list({ move_job_id: 'unlinked' })
        .then(r => r.data?.data ?? r.data ?? []),
    enabled: open,
  });
  const listFailed = queryLoadFailure(listQuery) != null;
  const q = search.trim().toLowerCase();
  const bols = ((listQuery.data ?? []) as any[]).filter(
    (b) => !q || String(b.bol_number ?? "").toLowerCase().includes(q),
  );

  const handleLink = async (bolId: string) => {
    setRefusal(null);
    try {
      await linkBol.mutateAsync({ moveJobId, bolId });
      toast.success("BOL linked to move job");
      qc.invalidateQueries({ queryKey: ['available-bols'] });
      onOpenChange(false);
    } catch (err) {
      setRefusal(getApiErrorMessage(err, "The BOL was not linked."));
      qc.invalidateQueries({ queryKey: ['available-bols'] });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Link BOL to Move Job</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fieldiq-muted" aria-hidden="true" />
            <Input
              className="pl-9"
              placeholder="Search BOL number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <p className="text-xs text-fieldiq-muted">Only BOLs on no move job are listed.</p>
          {refusal && (
            <p role="alert" data-testid="link-bol-refusal" className="text-sm font-medium text-fieldiq-text">
              {refusal}
            </p>
          )}
          <div className="max-h-64 overflow-y-auto space-y-1">
            {listFailed ? (
              <LoadFailureNotice error={queryFailureError(listQuery)} subject="BOLs" />
            ) : listQuery.isLoading ? (
              <div className="flex justify-center py-6" aria-hidden="true">
                <Loader2 className="h-5 w-5 animate-spin text-fieldiq-muted" />
              </div>
            ) : bols.length === 0 ? (
              <p className="text-sm text-fieldiq-muted text-center py-6">
                {q ? "No unlinked BOL matches that number" : "No BOLs are free to link"}
              </p>
            ) : (
              bols.map((bol: any) => (
                <button
                  key={bol.id}
                  onClick={() => handleLink(bol.id)}
                  disabled={linkBol.isPending}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-fi-control hover:bg-fieldiq-dark/50 transition-colors text-left"
                >
                  <div>
                    <p className="text-sm font-medium text-fieldiq-text font-mono">{bol.bol_number || "—"}</p>
                    <p className="text-[11px] text-fieldiq-muted">
                      {bol.customer_name || bol.shipper_name || "—"} &middot; {bol.status}
                    </p>
                  </div>
                  <Link2 className="w-4 h-4 text-fieldiq-muted" aria-hidden="true" />
                </button>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ── Main detail screen ── */
const MoveJobDetailScreen = () => {
  const timeZone = useTenantTimeZone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const jobQuery = useMoveJob(id || "");
  const { data: job, isLoading } = jobQuery;
  const generateMasterBol = useGenerateMasterBol();

  const [showLinkBol, setShowLinkBol] = useState(false);
  const [showSign, setShowSign] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const failure = queryLoadFailure(jobQuery);
  const back = () => navigate("/trucking/move-jobs");

  if (isLoading && !failure) {
    return (
      <div data-testid="move-job-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        <PageHeaderBand eyebrow="Move job" title="Move job" onBack={back} backLabel="Back to move jobs" />
        <div className="flex items-center justify-center py-24" aria-hidden="true">
          <Loader2 className="h-6 w-6 animate-spin text-fieldiq-muted" />
        </div>
      </div>
    );
  }

  // A failed read is not "not found". A 404 is; anything else says what happened.
  const raw = (job ?? null) as (MoveJob & Record<string, unknown>) | null;
  if (failure || !raw) {
    return (
      <div data-testid="move-job-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
        <PageHeaderBand eyebrow="Move job" title="Move job" onBack={back} backLabel="Back to move jobs" />
        <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)]">
          <div className="mx-auto max-w-[1920px] rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
            {failure ? (
              <LoadFailureNotice error={queryFailureError(jobQuery)} subject="this move job" />
            ) : (
              <p className="px-4 py-12 text-center text-sm text-fieldiq-muted">Move job not found</p>
            )}
          </div>
        </section>
      </div>
    );
  }

  const mj = raw;
  const bols: Record<string, unknown>[] = Array.isArray(mj.bols) ? (mj.bols as Record<string, unknown>[]) : [];
  const masterBol = ((raw as Record<string, unknown>).masterBol ?? null) as MasterBol;
  /** Linked loads that are signed: a tally of the list this route returns, which is every linked load. */
  const signedCount = bols.filter((b) => b.status === "signed").length;
  const totalCount = bols.length;
  const hasMasterBol = !!masterBol;
  /** The sign route requires `complete` and moves it to `finalized`. */
  const canSign = masterBol?.status === "complete";
  const isSigned = masterBol?.status === "finalized";

  const handleGenerateMasterBol = async () => {
    try {
      await generateMasterBol.mutateAsync(mj.id);
      toast.success("Master BOL generated");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to generate Master BOL"));
    }
  };

  // The tab is claimed BEFORE the await (a pop-up blocker refuses one opened
  // later), then pointed at the PDF fetched with the session's token.
  const handleDownloadPdf = async () => {
    const win = claimBlobTab();
    if (!win) {
      toast.error(POPUP_BLOCKED_MESSAGE);
      return;
    }
    setPdfLoading(true);
    try {
      const res = await api.trucking.moveJobs.masterBolPdf(mj.id);
      showBlobInTab(win, res.data);
    } catch (err) {
      win.close();
      toast.error(await getBlobApiErrorMessage(err, "Couldn't open the Master BOL PDF"));
    } finally {
      setPdfLoading(false);
    }
  };

  const operator = str((raw as Record<string, unknown>).operator_name);
  const coordinator = str((raw as Record<string, unknown>).coordinator_name);
  // The master BOL's own counts when there is one; otherwise the linked loads.
  const progressSigned = masterBol ? masterBol.loads_signed ?? null : signedCount;
  const progressTotal = masterBol ? masterBol.total_loads ?? null : totalCount;
  const fmtDay = (d: unknown) =>
    formatCalendarDate(str(d), { year: "numeric", month: "2-digit", day: "2-digit" }, "en-CA") ?? "—";

  return (
    <div data-testid="move-job-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Move job"
        title={mj.name || "—"}
        onBack={back}
        backLabel="Back to move jobs"
        actions={
          <>
            {/* No colour class on the secondary: it inherits the band's near-white. */}
            <button type="button" onClick={() => setShowLinkBol(true)} className="fi-action fi-action--standard border border-white/25 hover:border-white/60">
              <Link2 className="size-4" aria-hidden="true" />
              Link BOL
            </button>
            {/* Not offered when a master BOL already exists. */}
            {!hasMasterBol && (
              <button
                type="button"
                onClick={handleGenerateMasterBol}
                disabled={generateMasterBol.isPending || totalCount === 0}
                className="fi-action fi-action--standard fi-action--primary disabled:opacity-50"
              >
                {generateMasterBol.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <FileText className="size-4" aria-hidden="true" />}
                Generate Master BOL
              </button>
            )}
            {hasMasterBol && (
              <button type="button" onClick={handleDownloadPdf} disabled={pdfLoading} className="fi-action fi-action--standard border border-white/25 hover:border-white/60 disabled:opacity-50">
                {pdfLoading ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Download className="size-4" aria-hidden="true" />}
                Download Master BOL PDF
              </button>
            )}
          </>
        }
        stats={[
          { icon: <Truck className="size-4" aria-hidden="true" />, label: "Loads", sub: "Linked to this job", tone: "neutral", value: totalCount },
          { icon: <ClipboardCheck className="size-4" aria-hidden="true" />, label: "Signed", sub: "Linked loads signed", tone: "success", value: signedCount },
          { icon: <ListChecks className="size-4" aria-hidden="true" />, label: "Status", sub: "Move job", tone: "info", value: skin(JOB_STATUS, mj.status).label },
          { icon: <FileStack className="size-4" aria-hidden="true" />, label: "Master BOL", sub: masterBol ? skin(MASTER_STATUS, masterBol.status).label : "None yet", tone: "accent", value: masterBol ? masterBol.master_bol_number ?? "—" : "—" },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-5">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
              <div data-testid="move-job-progress" className="flex-shrink-0">
                <p className="text-[10px] font-bold uppercase tracking-wider text-fieldiq-muted">
                  {masterBol ? "Master BOL progress" : "Linked loads signed"}
                </p>
                <p className="mt-1 font-mono text-2xl font-extrabold text-fieldiq-text">
                  {progressSigned == null || progressTotal == null ? "—" : `${progressSigned}/${progressTotal}`}
                </p>
              </div>
              <dl className="grid flex-1 grid-cols-2 gap-x-10 gap-y-3 lg:grid-cols-3">
                {[
                  { label: "Operator", value: operator ?? "—" },
                  { label: "Rig", value: str(mj.rig_number) ?? "—" },
                  { label: "Coordinator", value: coordinator ?? "—" },
                  { label: "Origin LSD", value: str(mj.origin_lsd) ?? "—" },
                  { label: "Destination LSD", value: str(mj.destination_lsd) ?? "—" },
                  // `planned_start` is a CALENDAR DAY — see MoveJobsScreen.
                  { label: "Planned Start", value: fmtDay(mj.planned_start) },
                ].map((item) => (
                  <div key={item.label}>
                    <dt className="text-[10px] font-bold uppercase tracking-wider text-fieldiq-muted">{item.label}</dt>
                    <dd className="mt-0.5 text-sm text-fieldiq-text">{item.value}</dd>
                  </div>
                ))}
                {mj.notes && (
                  <div className="col-span-2 lg:col-span-3">
                    <dt className="text-[10px] font-bold uppercase tracking-wider text-fieldiq-muted">Notes</dt>
                    <dd className="mt-0.5 text-sm text-fieldiq-muted">{mj.notes}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          <section
            aria-labelledby="linked-bols-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Linked BOLs</p>
              <h2 id="linked-bols-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                Loads on this move
              </h2>
            </header>
            {totalCount === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 px-4 py-12">
                <p className="text-sm text-fieldiq-muted">No BOLs linked yet</p>
                <Button variant="outline" size="sm" onClick={() => setShowLinkBol(true)}>
                  <Link2 className="mr-1 h-4 w-4" /> Link a BOL
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-left">
                  <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                    <tr className="text-xs font-bold uppercase tracking-wider text-fieldiq-muted">
                      <th className="px-4 py-3">BOL number</th>
                      <th className="px-4 py-3">Truck</th>
                      <th className="px-4 py-3">Driver</th>
                      <th className="px-4 py-3">Load type</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Signed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bols.map((bol) => (
                      <tr key={String(bol.id)} className="border-t border-fieldiq-border">
                        <td className="px-4 py-3 font-mono text-sm text-fieldiq-text">{str(bol.bol_number) ?? "—"}</td>
                        {/* From the legacy `trucks` table, which has no rows (#803): a dash today. */}
                        <td className="px-4 py-3 text-xs text-fieldiq-muted">{str(bol.truck_unit_number) ?? "—"}</td>
                        <td className="px-4 py-3 text-xs text-fieldiq-text">{driverOf(bol) ?? "—"}</td>
                        <td className="px-4 py-3 text-xs text-fieldiq-muted">{getLoadTypeLabel(str(bol.load_type))}</td>
                        <td className="px-4 py-3"><LoadStatusBadge status={bol.status as string | null} /></td>
                        <td className="px-4 py-3 text-xs text-fieldiq-muted">{formatInstantDay(str(bol.signed_at), timeZone) ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
              {totalCount} linked · {displayValue(signedCount)} signed
            </p>
          </section>

                {/* Sign Master BOL section */}
          {canSign && (
            <div className="px-6 py-6">
              <div className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card p-5">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle2 className="w-5 h-5 text-fieldiq-text" aria-hidden="true" />
                  <h3 className="text-sm font-semibold text-fieldiq-text">Master BOL complete — ready for the operator&rsquo;s signature</h3>
                </div>
                {!showSign ? (
                  <Button
                    className="border border-black/20 bg-fi-signal-green text-fi-signal-ink hover:opacity-90"
                    onClick={() => setShowSign(true)}
                  >
                    <Pen className="w-4 h-4 mr-1" /> Sign Master BOL
                  </Button>
                ) : (
                  <div className="max-w-md space-y-3">
                    <MasterBolSignPanel moveJobId={mj.id} masterBol={masterBol} onSigned={() => setShowSign(false)} />
                    <Button variant="outline" onClick={() => setShowSign(false)}>
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {isSigned && (
            <div className="px-6 py-6">
              <div className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card p-5 flex items-center gap-3">
                <MasterBolSignedNote masterBol={masterBol} />
                <div className="ml-auto flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleDownloadPdf} disabled={pdfLoading}>
                    <Download className="w-4 h-4 mr-1" /> Download PDF
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Generate Invoice: POST /trucking/invoices/from-move-job once the master BOL is finalized. */}
          <TruckingInvoiceCard
            target={{ kind: "move_job", id: mj.id, ready: isSigned }}
            overlaps={bols.map((b) => ({ bol_number: typeof b.bol_number === "string" ? b.bol_number : null, overlap: b.rental_fee_overlap }))}
          />
        </div>
      </section>

      <LinkBolDialog open={showLinkBol} onOpenChange={setShowLinkBol} moveJobId={id || ""} />
    </div>
  );
};

export default MoveJobDetailScreen;
